import { useForm } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, type SelectOption } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { updateArtworkSchema } from "@/features/artwork/api/update-artwork";
import { CoverDropzone } from "@/features/artwork/components/cover-dropzone";
import { ImageDashboard } from "@/features/artwork/components/image-dashboard";
import {
  useRetrieveArtworkDraft,
  useUpdateArtworkMutation,
} from "@/features/artwork/queries";
import { ArtworkVisibility, CommentAllowance } from "@/features/artwork/types";
import { createFileRoute } from "@tanstack/react-router";
import { SendIcon, SquarePenIcon, TrophyIcon } from "lucide-react";

export const Route = createFileRoute("/artwork/create")({
  component: RouteComponent,
});

function RouteComponent() {
  const { data: artwork } = useRetrieveArtworkDraft({});
  const updateArtworkMutation = useUpdateArtworkMutation();

  const categories = [
    "2D像素艺术 / 2D Pixel Art",
    "3D像素艺术 / 3D Pixel Art",
    "像素艺术衍生 / Pixel Art Derivative",
    "其他 / Others",
  ];

  const specialTopics: SelectOption[] = [
    { value: "paa-art-award", label: "PAA 像素艺术大赛", icon: TrophyIcon },
  ];

  const tags: SelectOption[] = [
    { value: "pixel-dailies", label: "#Pixel_Dailies" },
    { value: "pixel-art", label: "#Pixel_Art" },
    { value: "pixel-art-community", label: "#Pixel_Art_Community" },
    { value: "pixel-art-artist", label: "#Pixel_Art_Artist" },
    { value: "love-and-peace", label: "#Love_And_Peace" },
  ];

  const form = useForm(updateArtworkSchema, {
    defaultValues: {
      artworkId: artwork?.id ?? "",
      title: artwork?.title,
      content: artwork?.content,
      coverFile: undefined,
      imageFiles: [],
      categories: artwork?.categories,
      tags: artwork?.tags,
      specialTopic: artwork?.specialTopic,
      publishStatus: artwork?.publishStatus,
      visibility: artwork?.visibility,
      commentAllowance: artwork?.commentAllowance,
    },
    onSubmit: async ({ value }) => {
      await updateArtworkMutation.mutateAsync(value);
    },
  });

  return (
    <form.Root className="container flex mx-auto">
      <div className="flex-1 p-4 space-y-4">
        <form.Field
          name="title"
          render={(field) => (
            <field.Container
              className="px-4 py-3 gap-3 bg-gray-subtle-bg [&>label]:text-sm"
              label="作品标题"
              showLabel
            >
              <Input placeholder="作品标题" className="h-12" variant="soft" />
            </field.Container>
          )}
        />

        <ImageDashboard />

        <form.Field
          name="content"
          render={(field) => (
            <field.Container
              className="px-4 py-3 gap-3 bg-gray-subtle-bg [&>label]:text-sm"
              label="作品描述"
              showLabel
            >
              <Textarea
                placeholder="给你的作品添加一下描述吧~"
                className="min-h-[100px]"
              />
            </field.Container>
          )}
        />

        <form.Field
          name="categories"
          render={(field) => (
            <field.Container
              className="px-4 py-3 gap-3 bg-gray-subtle-bg [&>label]:text-sm"
              label="分类"
              showLabel
            >
              <ToggleGroup type="multiple" className="flex-wrap gap-2">
                {categories.map((category) => (
                  <ToggleGroupItem key={category} value={category}>
                    {category}
                  </ToggleGroupItem>
                ))}
              </ToggleGroup>
            </field.Container>
          )}
        />

        <form.Field
          name="specialTopic"
          render={(field) => (
            <field.Container
              className="px-4 py-3 gap-3 bg-gray-subtle-bg [&>label]:text-sm"
              label="专题"
              showLabel
            >
              <Select className="h-12" options={specialTopics} search />
            </field.Container>
          )}
        />

        <form.Field
          name="tags"
          render={(field) => (
            <field.Container
              className="px-4 py-3 gap-3 bg-gray-subtle-bg [&>label]:text-sm"
              label="标签"
              showLabel
            >
              <Select className="h-12" options={tags} multiple search />
            </field.Container>
          )}
        />
      </div>

      <div className="p-4 space-y-4 w-3/10">
        <div className="space-y-2">
          <form.Submit className="w-full">
            <SendIcon />
            发布
          </form.Submit>
          <form.Submit className="w-full" intent="soft" flavor="gray" disabled>
            <SquarePenIcon />
            存草稿
          </form.Submit>
        </div>

        <div className="px-4 py-3 border-l-4 bg-gray-subtle-bg border-accent-solid [&>label]:text-sm">
          <Label>提示：</Label>
          <p className="mt-2 text-xs text-gray-fg-secondary">
            投稿前请确定已经阅读《投稿规范》与《免责声明》，发布作品即同意以上条款。
          </p>
        </div>

        <form.Field
          name="coverFile"
          render={(field) => (
            <field.Container
              className="px-4 py-3 gap-3 bg-gray-subtle-bg [&>label]:text-sm"
              label="作品封面"
              showLabel
            >
              <CoverDropzone className="w-full aspect-square" />
            </field.Container>
          )}
        />

        <form.Field
          name="visibility"
          render={(field) => (
            <field.Container
              className="px-4 py-3 gap-3 bg-gray-subtle-bg [&>label]:text-sm"
              label="隐私设置"
              showLabel
            >
              <Select
                options={[
                  { value: ArtworkVisibility.Public, label: "公开" },
                  {
                    value: ArtworkVisibility.FriendsOnly,
                    label: "仅好友可见",
                  },
                  {
                    value: ArtworkVisibility.Private,
                    label: "私密",
                  },
                ]}
              />
            </field.Container>
          )}
        />

        <form.Field
          name="commentAllowance"
          render={(field) => (
            <field.Container
              className="px-4 py-3 gap-3 bg-gray-subtle-bg [&>label]:text-sm"
              label="评论设置"
              showLabel
            >
              <Select
                options={[
                  {
                    value: CommentAllowance.All,
                    label: "允许所有评论",
                  },
                  {
                    value: CommentAllowance.FriendsOnly,
                    label: "仅好友可评论",
                  },
                  {
                    value: CommentAllowance.None,
                    label: "禁止评论",
                  },
                ]}
              />
            </field.Container>
          )}
        />
      </div>
    </form.Root>
  );
}
